import {
	Component,
	Input,
	ViewEncapsulation,
	OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { max } from 'd3-array';
import { scaleLinear, scaleBand, ScaleBand, ScaleLinear } from 'd3-scale';
import { NgIf } from '@angular/common';

import { safe } from '../../../../shared/utils/safe';
import { ClaimStatus } from '../../../models/quality-issue';

import { QualityFeedback } from './quality-feedback.model';

const EMPTY = '';
const ZERO = 0;

@Component({
	selector: 'copo-quality-feedback-bars',
	templateUrl: './quality-feedback-bars.component.html',
	styleUrl: './quality-feedback-bars.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [NgIf]
})
export class QualityFeedbackBarsComponent implements OnInit {
	@Input() data?: QualityFeedback[];

	ready = false;
	barY = 90;
	elementWidth = 225;
	barWidth = 35;
	barHeight = 80;

	private x!: ScaleBand<string>;
	private y!: ScaleLinear<number, number>;

	constructor(private readonly router: Router) {}

	ngOnInit() {
		this.createChart();
	}

	topRoundedColumn(barIndex: number) {
		if (!this.data) {
			return '';
		}
		const data = this.data[barIndex - 1];
		if (data.total > 0) {
			const x = (this.x.bandwidth() / 2) - (this.barWidth / 2);
			const height = this.barHeight - this.y(data.total);
			const width = this.barWidth;
			const radius = height <= 5 ? 1 : 5;
			const heightBeforeArc = height <= 5 ? 1 : height - radius;
			const newWidth = width - 10;
			return (
				`M${ x },${ this.barY } ` // Mx,y Move the pen to(x, y)
				+ `v-${ heightBeforeArc } ` // h<length> Draw a vertical line of length <height>px
				+ `a ${ radius },${ radius } 0 0 1 ${ radius },-${ radius } ` // arc
				+ `h${ newWidth } `
				+ `a ${ radius },${ radius } 0 0 1 ${ radius },${ radius } `
				+ `v${ heightBeforeArc } `
				+ 'z' // close shape
			);
		}
		return '';
	}

	async redirect(barIndex: number) {
		if (!this.data) {
			return;
		}
		const barData = this.data[barIndex - 1];
		if (barData.redirect) {
			await this.router.navigate(
				[barData.redirect],
				{ state: this.viewDetailsState(barData.status) },
			);
		}
	}

	viewDetailsState(value: ClaimStatus) {
		const filter: (
			{
				id: string;
				values: {
					apiName: string;
					selected: boolean;
					value: string;
				}[][];
			}
		)[] = [
			{
				id: 'status',
				values: [
					[
						ClaimStatus.DRAFT,
						ClaimStatus.PENDING,
						ClaimStatus.CREATED,
						ClaimStatus.CANCELLED,
						ClaimStatus.PAID,
						ClaimStatus.REFUSED,
						ClaimStatus.REGWARNING
					].map(status => ({
						apiName: 'status',
						selected: value === status,
						value: status
					}))
				]
			}
		];

		return {
			filter
		};
	}

	getAmount(barIndex: number) {
		if (this.data) {
			return this.data[barIndex - 1].amount;
		}
		return EMPTY;
	}

	getTotal(barIndex: number) {
		if (this.data) {
			return this.data[barIndex - 1].total;
		}
		return ZERO;
	}

	getType(barIndex: number) {
		if (this.data) {
			return this.data[barIndex - 1].type;
		}
		return EMPTY;
	}

	private createChart() {
		if (!this.data) {
			return;
		}

		this.x = scaleBand()
			.rangeRound([0, this.elementWidth])
			.domain(['1', '2']);

		this.y = scaleLinear()
			.rangeRound([this.barHeight, 0])
			.domain([0, safe(max(this.data, (d: QualityFeedback) => d.total))]);

		this.ready = true;
	}
}
